/**
 * Choix des répétitions — **une roue à accrochage**, sur le modèle du
 * sélecteur d'iOS *(05/09/2026, demandé après usage : « comme l'app alarme »)*.
 *
 * 🔴 **Pourquoi une roue plutôt qu'un clavier.** Les répétitions sont la valeur
 * la plus souvent saisie de la séance, et c'est la seule des trois qui soit un
 * **petit entier borné** — on en fait entre 1 et quelques dizaines, jamais 137.
 * Un clavier numérique demande de viser deux touches puis de refermer, sur un
 * écran qu'il recouvre à moitié ; une roue demande un glissement du pouce, sans
 * rien masquer. C'est le même raisonnement que le RPE le même jour, appliqué à
 * une grandeur qui, elle, se compte.
 *
 * ⚠️ **La charge garde son clavier**, et ce n'est pas une inconséquence : elle
 * est décimale (62,5) et sa plage utile va de 1 à plus de 200 — une roue y
 * demanderait de faire défiler des centaines de crans. Une roue n'est un
 * progrès que sur un domaine court.
 *
 * ⚠️ **La DURÉE aussi garde son clavier**, pour la même raison. Un gainage se
 * compte en dizaines de secondes, jusqu'à plusieurs minutes : une roue de 300
 * crans n'est plus une roue, et l'arrondir à des pas de 5 s ferait perdre la
 * précision que la routine prescrit. La colonne change donc de contrôle selon
 * ce qu'elle mesure — ce sont deux grandeurs différentes, et l'en-tête le dit
 * déjà (`REPS` contre `SEC`).
 *
 * ── L'ACCROCHAGE ──────────────────────────────────────────────────────────
 *
 * 🔴 **`scroll-snap-type` natif, jamais un défilement piloté en JavaScript.**
 * C'est ce qui donne l'inertie d'iOS, le rebond, et le comportement au doigt
 * qu'on attend d'une roue — trois choses qu'une implémentation manuelle
 * approche sans jamais atteindre. Le composant ne fait que **lire** où le
 * défilement s'est arrêté.
 *
 * ⚠️ **Chaque cran fait exactement `--wheel-item`**, et le calcul de l'index en
 * dépend : `Math.round(scrollTop / cran)`. Une hauteur posée ailleurs qu'ici
 * ferait diverger le rendu et la lecture, donc afficherait une valeur au centre
 * et en retiendrait une autre. La valeur vit dans le CSS, et ce module la relit
 * du DOM plutôt que de la recopier.
 */

import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/nourri';
import { Sheet } from '../../components/ui/primitives';
import { repsValues, valeurDuFiligrane } from './setValues';

export function RepsSheet({
  courant,
  propose,
  onPick,
  onClose,
}: {
  /** Ce qui est saisi, ou `''`. */
  courant: string;
  /** Ce que la ligne propose en filigrane — le point de départ de la roue. */
  propose: string;
  onPick: (valeur: string) => void;
  onClose: () => void;
}) {
  const piste = useRef<HTMLDivElement | null>(null);

  /*
   * ⚠️ **`valeurDuFiligrane` et non `Number`** *(06/09/2026)* : depuis que la
   * plage prescrite sert de filigrane, `propose` peut valoir « 8-10 ».
   * `Number('8-10')` rend `NaN`, et la roue se serait ouverte sur son premier
   * cran — c'est-à-dire loin de ce qu'on venait faire.
   */
  const depart = courant !== '' ? Number(courant) : (valeurDuFiligrane(propose) ?? Number.NaN);
  const valeurs = repsValues(Number.isFinite(depart) ? depart : null);

  /*
   * ⚠️ **Le rang de départ est calculé au premier rendu, pas dans un effet.**
   * Un effet qui poserait l'état ferait un rendu de plus et tomberait sous
   * `react-hooks/set-state-in-effect` — la règle qui avait déjà imposé une
   * `key` plutôt qu'un pré-remplissage différé à l'étape 6.
   */
  const rangInitial = (() => {
    const i = valeurs.indexOf(Number.isFinite(depart) ? depart : NaN);
    if (i >= 0) return i;
    // Rien de saisi ni de proposé : on ouvre sur 10, le milieu de ce qu'on fait.
    const dix = valeurs.indexOf(10);
    return dix >= 0 ? dix : 0;
  })();

  const [rang, setRang] = useState(rangInitial);

  /*
   * 🔴 **Le défilement initial est posé sans animation**, au montage : la roue
   * doit être **déjà** sur la bonne valeur quand la feuille apparaît. C'est la
   * même exigence que l'onglet actif amené dans la vue (`SectionTabs`), et pour
   * la même raison — au premier rendu il n'y a rien à animer.
   *
   * ⚠️ La hauteur d'un cran est **relue du DOM**, jamais recopiée : elle vit
   * dans le CSS (`--wheel-item`), et deux définitions finiraient par diverger,
   * donc par afficher une valeur au centre et en retenir une autre.
   */
  useEffect(() => {
    const el = piste.current;
    if (el === null) return;
    const cran = el.firstElementChild?.clientHeight ?? 44;
    el.scrollTop = rangInitial * cran;
    // Une seule fois, à l'ouverture : la suite appartient au doigt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = () => {
    const el = piste.current;
    if (el === null) return;
    const cran = el.firstElementChild?.clientHeight ?? 44;
    const i = Math.max(0, Math.min(valeurs.length - 1, Math.round(el.scrollTop / cran)));
    if (i !== rang) setRang(i);
  };

  const choisi = valeurs[rang] ?? valeurs[0];

  return (
    <Sheet open onClose={onClose} title="Répétitions">
      {/*
        ⚠️ Le cadre porte la bande centrale, en pseudo-élément **non
        cliquable** : elle marque la position retenue sans jamais intercepter le
        doigt qui fait tourner la roue.
      */}
      <div className="n-wheel-frame">
        <div className="n-wheel" ref={piste} onScroll={onScroll}>
          {valeurs.map((valeur, i) => (
            /*
              ⚠️ **Un bouton par cran, et pas un simple `<div>`.** Une roue ne se
              tabule pas : sans contrôle focalisable, la valeur serait
              inatteignable au clavier et muette pour un lecteur d'écran. Le tap
              amène le cran au centre — le comportement d'iOS —, il ne valide
              pas : c'est « Valider » qui engage, et lui seul.
            */
            <button
              type="button"
              key={valeur}
              className={`n-wheel__item${i === rang ? ' is-current' : ''}`}
              aria-current={i === rang ? 'true' : undefined}
              onClick={() => {
                const el = piste.current;
                if (el === null) return;
                const cran = el.firstElementChild?.clientHeight ?? 44;
                el.scrollTo({ top: i * cran, behavior: 'smooth' });
              }}
            >
              {valeur}
            </button>
          ))}
        </div>
      </div>

      {/*
        🔴 **Un seul bouton, et il porte la teinte de l'action** *(06/09/2026)*.

        La feuille en a porté deux quelques heures : « Valider » et « Effacer »,
        d'abord identiques (tous deux en `secondary`, donc rien ne disait lequel
        engageait), puis différenciés — avant qu'« Effacer » ne soit retiré tout
        court, demandé après usage.

        ⚠️ **Ce qu'on perd, et il faut le savoir** : on ne peut plus ramener une
        cellule à son filigrane depuis la roue. En pratique ça ne change rien à
        ce qui s'écrit — le filigrane vaut le haut de la plage, et le choisir
        explicitement donne la même valeur (`valeurDuFiligrane`). Ce qui
        disparaît est la nuance d'affichage entre « pas encore saisi » et
        « saisi à la valeur proposée ».

        ⚠️ **Le RPE garde son « Aucun RPE »**, et ce n'est pas une
        inconséquence : un RPE absent est un cas **courant et légitime** (on
        oublie de le noter, la moyenne de séance l'exclut alors), là où une série
        faite sans nombre de répétitions n'est pas une série faite.
      */}
      <Button
        variant="primary"
        onClick={() => {
          onPick(String(choisi));
        }}
      >
        Valider {choisi}
      </Button>
    </Sheet>
  );
}
